'use client'

import { cn } from '@/utilities/ui'
import React, { useEffect, useRef, useState } from 'react'
import { preload } from 'react-dom'

import type { Props as MediaProps } from '../types'

import { getClientSideURL } from '@/utilities/getURL'
import { getVideoPosterUrl } from '@/utilities/getVideoPosterUrl'

/**
 * Autoplaying decorative video, but network-lazy: nothing is fetched until the
 * element approaches the viewport. Several pages carry multiple multi-MB clips,
 * and with autoplay + preload="auto" they all downloaded on page load — tens of
 * MB and most of the LCP damage. The poster covers the element until playback
 * starts, and playback pauses again once the clip scrolls away.
 */
const NEAR_VIEWPORT_MARGIN = '200px'

/** Poster renditions, narrowest first (see imageSizes in collections/Media.ts). */
const POSTER_RENDITIONS = ['thumbnail', 'small', 'medium', 'large', 'xlarge'] as const

export const VideoMedia: React.FC<MediaProps> = (props) => {
  const { alt: altFromProps, onClick, priority, resource, videoClassName } = props

  const videoRef = useRef<HTMLVideoElement>(null)

  // A `poster` is fetched as soon as it is in the markup — the attribute has no
  // lazy equivalent — so below-the-fold clips withhold theirs until they come
  // near the viewport. A hero's poster is the LCP element and is never withheld.
  const [nearViewport, setNearViewport] = useState(Boolean(priority))

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    video.muted = true

    let observer: IntersectionObserver | undefined

    const watch = () => {
      if (!('IntersectionObserver' in window)) {
        setNearViewport(true)
        video.play().catch(() => {})
        return
      }

      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              setNearViewport(true)
              video.play().catch(() => {})
            } else if (!video.paused) {
              video.pause()
            }
          }
        },
        { rootMargin: NEAR_VIEWPORT_MARGIN },
      )

      observer.observe(video)
    }

    // Playback pulls the whole file down the moment it starts, and in a hero that
    // lands while the LCP poster is still painting — on mobile the clip and the
    // image compete for the same bandwidth (the homepage's is 491 KB, the single
    // heaviest request on the page). The poster already covers the element, so
    // playback waits for the rest of the page to finish loading.
    if (document.readyState === 'complete') {
      watch()
    } else {
      window.addEventListener('load', watch, { once: true })
    }

    return () => {
      window.removeEventListener('load', watch)
      observer?.disconnect()
    }
  }, [])

  if (resource && typeof resource === 'object') {
    const { description, filename, mimeType, title, url } = resource
    // Same cache tag ImageMedia uses: replacing a file (e.g. re-compressing a
    // video) changes updatedAt, so the URL changes and the CDN's cached copy
    // of the old file stops being served.
    const cacheTag = resource.updatedAt ? `?${resource.updatedAt}` : ''
    const src = url
      ? `${getClientSideURL()}${url}${cacheTag}`
      : `${getClientSideURL()}/media/${filename}${cacheTag}`
    const accessibleName = altFromProps || description || title || undefined
    // The poster's own Media doc: used to size the element, and to pick a
    // rendition instead of the full-size frame. `poster` takes a single URL,
    // so it can't be a responsive srcset — one width has to serve everyone.
    // Full-size posters were among the heaviest images on several pages (a
    // 1280x720 frame displayed at 316x178). A hero renders near full width,
    // an in-page clip much smaller.
    const posterDoc =
      resource.videoThumbnail && typeof resource.videoThumbnail === 'object'
        ? resource.videoThumbnail
        : undefined
    const posterTargetWidth = priority ? 900 : 600
    const posterRendition = POSTER_RENDITIONS.map((name) => posterDoc?.sizes?.[name]).find(
      (size) => size?.url && (size.width ?? 0) >= posterTargetWidth,
    )
    const poster = posterRendition?.url
      ? `${getClientSideURL()}${posterRendition.url}`
      : getVideoPosterUrl(resource, getClientSideURL())

    // In a hero the poster is the LCP element, but `poster` has no fetchpriority
    // attribute, so the browser discovers it late and at default priority.
    // A high-priority preload in <head> fixes both.
    if (priority && poster) preload(poster, { as: 'image', fetchPriority: 'high' })

    // Reserve the element's space before anything loads — a video with no
    // intrinsic size renders at 0 height and the page shifts when it appears.
    // The poster is a frame of the video, so its dimensions are a valid stand-in.
    const intrinsicWidth = resource.width || posterDoc?.width
    const intrinsicHeight = resource.height || posterDoc?.height
    const aspectStyle =
      intrinsicWidth && intrinsicHeight
        ? { aspectRatio: `${intrinsicWidth} / ${intrinsicHeight}` }
        : undefined

    return (
      <video
        style={aspectStyle}
        aria-label={accessibleName}
        className={cn('w-full h-full object-cover', videoClassName)}
        loop
        muted
        onClick={onClick}
        playsInline
        poster={nearViewport ? poster : undefined}
        // With a poster the first frame is already covered; without one, fetch
        // just enough metadata that browsers paint a frame instead of a black box.
        // A clip whose poster is only deferred still counts as having one.
        preload={poster ? 'none' : 'metadata'}
        ref={videoRef}
        title={title ?? undefined}
      >
        <source src={src} type={mimeType ?? undefined} />
      </video>
    )
  }

  return null
}
